import {type FC, useLayoutEffect} from 'react';
import {Line} from 'react-chartjs-2';

import {
  CategoryScale,
  Chart,
  Filler,
  LinearScale,
  LineElement,
  PointElement,
  Tooltip,
} from 'chart.js';
import Color from 'color';
import {useTheme} from 'styled-components';

import {useI18n} from '@yourcoach/i18n';

import type {MetricModel} from '../../../../../core/domain/Metric/Metric.model';
import type {Period} from '../../../types';

import NoChartDataSvg from '../../../../../assets/icons/empty-state/no-chart-data.svg';
import {NoResultsView} from '../../../../../components/NoResultsView';
import {dataFactoriesByPeriod} from './utils';

type Props = {
  metrics: MetricModel[];
  period: Period;
  unit: MetricModel.WeightUnit;
};

export const WeightChart: FC<Props> = ({metrics, period, unit}) => {
  const {t} = useI18n();
  const theme = useTheme();

  useLayoutEffect(() => {
    Chart.register(
      LineElement,
      Tooltip,
      CategoryScale,
      LinearScale,
      PointElement,
      Filler,
    );

    return () =>
      Chart.unregister(
        LineElement,
        Tooltip,
        CategoryScale,
        LinearScale,
        PointElement,
        Filler,
      );
  }, []);

  const {data, labels, metricsByDateLabel} = dataFactoriesByPeriod[period]({
    metrics,
    period,
    unit,
  });

  return data.length ? (
    <Line
      data={{
        datasets: [
          {
            backgroundColor: ctx => {
              const c = ctx.chart.ctx;
              const gradient = c.createLinearGradient(
                0,
                0,
                0,
                ctx.chart.chartArea.height,
              );

              gradient.addColorStop(
                0,
                Color(theme.color.v3.static.accent.trackers)
                  .alpha(0.6)
                  .toString(),
              );
              gradient.addColorStop(
                1,
                Color(theme.color.v3.static.accent.trackers)
                  .alpha(0.1)
                  .toString(),
              );

              return gradient;
            },
            borderColor: theme.color.v3.static.accent.trackers,
            data,
            fill: true,
            pointBackgroundColor: theme.color.v3.static.accent.trackers,
            pointBorderColor: theme.color.v3.static.accent.trackers,
            pointHoverRadius: 5,
            spanGaps: true,
          },
        ],
        labels,
      }}
      options={{
        maintainAspectRatio: false,
        plugins: {
          tooltip: {
            bodyFont: {size: 14},
            callbacks: {
              label: i => i.parsed.y.toString(),
              title: i =>
                period === 'week'
                  ? ''
                  : metricsByDateLabel.get(i[0]!.label)?.date.format('LL'),
            },
            displayColors: false,
          },
        },
        responsive: true,
        scales: {
          x: {
            grid: {
              color: theme.color.v2.border2,
            },
          },
          y: {grid: {display: false}},
        },
      }}
    />
  ) : (
    <NoResultsView
      color="icon.gray"
      icon={NoChartDataSvg}
      iconColor="icon.gray"
      iconSize={100}
      text={t('shared:no_data_added')}
    />
  );
};
